import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import clsx from 'clsx';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { InactivityGuard } from '@/components/InactivityGuard';
import { Spinner } from '@/components/ui/primitives';
import { ThemeToggle } from '@/lib/theme';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from './NotificationBell';

interface NavItem {
  label: string;
  to: string;
  permission?: string;
  children?: Array<{ label: string; to: string; permission?: string }>;
}

/**
 * Navigation, Lumina style: a frosted 248px sidebar with a warm glow along its
 * left edge. Items get rounded hover states; the active item is a frosted
 * indigo pill with a small orange dot as the key indicator.
 */
const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/', permission: 'dashboard.view' },
  { label: 'Tasks', to: '/tasks' },
  {
    label: 'Products', to: '/products', permission: 'products.view',
    children: [
      { label: 'Models', to: '/products/models' },
      { label: 'Brands', to: '/products/brands' },
      { label: 'Option groups', to: '/products/option-groups' },
      { label: 'Ancillary charges', to: '/products/ancillary-charges' },
      { label: 'Parts', to: '/products/parts', permission: 'parts.view' },
    ],
  },
  {
    label: 'Procurement', to: '/procurement', permission: 'procurement.view',
    children: [
      { label: 'Purchase orders', to: '/procurement/purchase-orders' },
      { label: 'Shipments', to: '/procurement/shipments' },
      { label: 'Suppliers', to: '/procurement/suppliers' },
    ],
  },
  {
    label: 'Warehouse', to: '/warehouse', permission: 'warehouse.view',
    children: [
      { label: 'Machine stock', to: '/warehouse/machines' },
      { label: 'Goods receipt', to: '/warehouse/goods-receipt', permission: 'warehouse.goods_receipt' },
      { label: 'QC / PDI', to: '/warehouse/qc', permission: 'warehouse.qc' },
      { label: 'Fulfilment', to: '/warehouse/fulfilment' },
      { label: 'Deliveries', to: '/warehouse/deliveries' },
      { label: 'Parts stock', to: '/warehouse/parts', permission: 'parts.view' },
      // Same page as Sales → Reservations — Warehouse only ever views it, to
      // know what's already committed against stock still inbound.
      { label: 'Reservations', to: '/sales/reservations', permission: 'reservations.view' },
    ],
  },
  {
    label: 'Sales', to: '/sales', permission: 'sales.view',
    children: [
      { label: 'Pipeline', to: '/sales/pipeline' },
      { label: 'Opportunities', to: '/sales/opportunities' },
      { label: 'Quotes', to: '/sales/quotes', permission: 'quotes.view' },
      { label: 'Proformas', to: '/sales/proformas', permission: 'orders.view' },
      { label: 'Orders', to: '/sales/orders', permission: 'orders.view' },
      { label: 'Invoices', to: '/sales/invoices', permission: 'invoices.view' },
      { label: 'Reservations', to: '/sales/reservations', permission: 'reservations.view' },
    ],
  },
  { label: 'Trials', to: '/trials', permission: 'trials.view' },
  {
    label: 'Customers', to: '/customers', permission: 'customers.view',
    children: [
      { label: 'All customers', to: '/customers' },
      { label: 'Pricing groups', to: '/customers/pricing-groups' },
      { label: 'Site surveys', to: '/customers/site-assessments' },
    ],
  },
  { label: 'Machines', to: '/machines', permission: 'machines.view' },
  {
    label: 'Service', to: '/service', permission: 'service.view',
    children: [
      { label: 'Warranties', to: '/service/warranties' },
      { label: 'Service log', to: '/service/log' },
      { label: 'Claims', to: '/service/claims' },
    ],
  },
  {
    label: 'Documents', to: '/documents', permission: 'documents.view',
    children: [
      { label: 'Library', to: '/documents/library' },
      { label: 'Generated', to: '/documents/generated' },
      { label: 'Contracts', to: '/documents/contracts' },
      { label: 'Templates', to: '/documents/templates' },
    ],
  },
  { label: 'Reports', to: '/reports', permission: 'reports.view' },
  {
    label: 'Settings', to: '/settings', permission: 'settings.view',
    children: [
      { label: 'Company', to: '/settings/company' },
      { label: 'Users & roles', to: '/settings/users', permission: 'users.manage' },
      { label: 'My security', to: '/settings/security' },
      { label: 'Integrations', to: '/settings/integrations' },
      { label: 'Deleted items', to: '/settings/deleted-items', permission: 'reports.financial' },
      { label: 'Tasks', to: '/settings/tasks' },
      { label: 'Data import', to: '/settings/import', permission: 'customers.manage' },
      { label: 'Audit log', to: '/settings/audit-log', permission: 'audit.view' },
    ],
  },
];

export function Shell() {
  const { user, can, signOut, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [quick, setQuick] = useState<{
    favourites: Array<{ entityType: string; entityId: string; label: string }>;
    recent: Array<{ entityType: string; entityId: string; label: string; viewedAt: string }>;
  }>({ favourites: [], recent: [] });

  useEffect(() => {
    if (!isAuthenticated) return;
    void api.get<typeof quick>('/api/favourites').then(setQuick).catch(() => undefined);
  }, [isAuthenticated, location.pathname]);

  // Close the mobile drawer on navigation — leaving it open over the new page
  // is a common and irritating bug.
  useEffect(() => setIsMobileNavOpen(false), [location.pathname]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) navigate('/sign-in', { replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!isAuthenticated || !user) return null;

  const visible = NAV.filter((item) => !item.permission || can(item.permission));

  const hrefFor = (entityType: string, entityId: string) => {
    const map: Record<string, string> = {
      Machine: '/machines', Customer: '/customers', Quote: '/sales/quotes',
      Order: '/sales/orders', SalesOrder: '/sales/orders', Model: '/products/models',
      Opportunity: '/sales/opportunities', Trial: '/trials',
    };
    return `${map[entityType] ?? '/'}/${entityId}`;
  };

  const initials = user.name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex min-h-full">
      {/* ─── Sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={clsx(
          'glass sidebar-glow fixed inset-y-0 left-0 z-40 flex w-sidebar shrink-0 flex-col border-r border-rule',
          'transition-transform duration-base ease-lumina md:sticky md:top-0 md:h-screen md:translate-x-0',
          isMobileNavOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Link to="/" className="relative flex items-center gap-2.5 px-6 pb-5 pt-6">
          <img src="/artemis-logo.svg" alt="" aria-hidden className="h-[26px] w-auto shrink-0" />
          <span className="font-sans text-[15px] font-semibold tracking-[-0.01em] text-ink">VendFlow</span>
        </Link>

        <nav className="relative flex-1 space-y-4 overflow-y-auto px-3 pb-4">
          {visible.map((item) => {
            const children = item.children?.filter((c) => !c.permission || can(c.permission)) ?? [];
            return (
              <div key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-2 rounded-md px-3 py-1.5 font-sans text-[14px] transition-colors duration-fast',
                      isActive
                        ? 'glass-strong bg-secondary/[0.08] font-medium text-secondary shadow-sm dark:bg-secondary/20 dark:text-[#A5B4FC]'
                        : 'text-ink-secondary hover:bg-surface-hover/80 hover:text-ink dark:hover:bg-ink/[0.04]',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* The orange dot — the key indicator, and the only
                          orange in the navigation. */}
                      <span
                        aria-hidden
                        className={clsx(
                          'h-1.5 w-1.5 shrink-0 rounded-full transition-colors',
                          isActive ? 'bg-primary' : 'bg-transparent',
                        )}
                      />
                      {item.label}
                    </>
                  )}
                </NavLink>

                {children.length > 0 && location.pathname.startsWith(item.to) && item.to !== '/' && (
                  <div className="mt-1 space-y-0.5 pl-6">
                    {children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end
                        className={({ isActive }) =>
                          clsx(
                            'block rounded-md px-2.5 py-1 text-caption transition-colors duration-fast',
                            isActive
                              ? 'font-medium text-secondary dark:text-[#A5B4FC]'
                              : 'text-ink-tertiary hover:bg-surface-hover/60 hover:text-ink-secondary dark:hover:bg-ink/[0.04]',
                          )
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ─── User block ──────────────────────────────────────────────── */}
        <div className="relative border-t border-rule px-4 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-fill font-sans text-[11px] font-semibold text-white">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="truncate font-sans text-body-sm font-medium text-ink">{user.name}</div>
              <span className="mt-0.5 inline-block rounded-full bg-ink/[0.06] px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-[0.04em] text-ink-secondary dark:bg-ink/10">
                {user.role.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
          <button
            onClick={() => void signOut().then(() => navigate('/sign-in'))}
            className="mt-3 font-sans text-caption text-ink-tertiary transition-colors hover:text-secondary"
          >
            Sign out
          </button>
        </div>
      </aside>

      {isMobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 backdrop-blur-[2px] md:hidden"
          onClick={() => setIsMobileNavOpen(false)}
          role="presentation"
        />
      )}

      {/* ─── Main column ─────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print glass sticky top-0 z-20 border-b border-rule">
          <div className="mx-auto flex h-topbar max-w-content items-center gap-4 px-page-sm md:px-page">
            <button
              onClick={() => setIsMobileNavOpen((v) => !v)}
              aria-label="Toggle navigation"
              className="label md:hidden"
            >
              Menu
            </button>
            <GlobalSearch />
            <div className="ml-auto flex items-center gap-1.5">
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>

          {(quick.favourites.length > 0 || quick.recent.length > 0) && (
            <div className="hidden border-t border-divider lg:block">
              <div className="mx-auto flex max-w-content flex-wrap items-center gap-x-5 gap-y-1 px-page py-1.5 text-caption">
                {quick.favourites.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="label">Pinned</span>
                    {quick.favourites.slice(0, 5).map((f) => (
                      <Link
                        key={`${f.entityType}-${f.entityId}`}
                        to={hrefFor(f.entityType, f.entityId)}
                        className="rounded-full bg-ink/[0.04] px-2.5 py-0.5 text-ink-secondary transition-colors hover:bg-secondary/[0.08] hover:text-secondary dark:bg-ink/[0.08]"
                      >
                        {f.label}
                      </Link>
                    ))}
                  </div>
                )}
                {quick.recent.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="label">Recent</span>
                    {quick.recent.slice(0, 5).map((r) => (
                      <Link
                        key={`${r.entityType}-${r.entityId}`}
                        to={hrefFor(r.entityType, r.entityId)}
                        className="rounded-full px-2 py-0.5 text-ink-tertiary transition-colors hover:bg-ink/[0.04] hover:text-ink"
                      >
                        {r.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </header>

        <main className="flex-1 px-page-sm py-page-sm md:px-page md:py-page">
          <div className="mx-auto max-w-content">
            {/* Keyed on the route so navigating to a different page always
                remounts the boundary — otherwise its `error` state would
                keep showing the crashed page's fallback even after the
                sidebar (which lives outside the boundary) had already
                routed somewhere healthy. */}
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <InactivityGuard />
    </div>
  );
}
